// File: app/src/utils/mixedStyle.js
//
// The two session SELECTORS: "Surprise me" (stored as "mixed") and "What's
// next" ("next"). Neither is a shape the model can build — each is a request
// for one, resolved here into 1-3 concrete styles before the prompt is built.
//
// "Mixed" used to be a vague prompt instruction, and the model answered it the
// same way every time (warm-up, two strength supersets, circuit finisher).
// Resolving it against a table of coherent session shapes fixed the sameness;
// issue #83 then split the one selector into two different requests:
//
//   Surprise me — variety. Ranks shapes by how long since their styles were
//                 trained (least recently used, never the previous shape).
//   What's next — need. Ranks shapes by what the week is missing against
//                 evidence-based weekly targets (utils/trainingBalance.js),
//                 and says why.
//
// Both filter the library by hard limits first (time, equipment, energy, and
// shapes the user has hidden), then weight by the user's training preferences.
//
// Variety comes from rotation, not randomness. A uniform draw over a dozen
// shapes repeats noticeably often, and a repeat is far more noticeable than
// the variety around it.

import { formatLabel } from "./formatLabel";
import { describeUndertrained } from "./muscleVolume";
import {
  STYLE_ADAPTATION,
  describeNeeds,
  needFor,
  summariseWeeklyBalance,
} from "./trainingBalance";

export const MIXED_STYLE = "mixed";
export const NEXT_STYLE = "next";
export const SELECTOR_STYLES = [MIXED_STYLE, NEXT_STYLE];

// The session-shape library. Each shape is a combination that stands up
// physiologically on its own — the ordering within `styles` is irrelevant
// (STYLE_BLOCK_ORDER in prompts.js sequences them), but the pairing is the
// point. Tags drive filtering and weighting:
//   minMinutes  shortest session the shape fits without being a token gesture
//   needsLoad   needs external load to work as intended (heavy strength)
//   fatigue     1 easy · 2 moderate · 3 demanding — Low energy excludes 3
//   dense       little standing around — favoured by Pair exercises / Dense
//   trains      weekly-target adaptations, when they differ from the styles'
//   glue        how the blocks relate, so the session reads as one design
//               rather than two workouts stapled together
export const MIXED_ARCHETYPES = [
  {
    id: "strength-metcon",
    name: "Strength + metcon finisher",
    styles: ["strength", "hiit"],
    minMinutes: 30,
    needsLoad: true,
    fatigue: 3,
    dense: false,
    glue: "The strength block is the main event. The HIIT finisher is short (6-10 min) and complements the lifts rather than repeating them: after lower-body lifting use an erg or upper-body movements, after upper-body lifting use legs or an erg.",
  },
  {
    id: "power-circuit",
    name: "Power primer + circuit",
    styles: ["power", "circuit"],
    minMinutes: 25,
    needsLoad: false,
    fatigue: 2,
    dense: false,
    glue: "The power primer is short and crisp, done fresh with full recovery. The circuit then builds on the same movement themes at lower intensity (e.g. jumps, then squats and lunges in the circuit).",
  },
  {
    id: "hypertrophy-conditioning",
    name: "Hypertrophy + conditioning",
    styles: ["hypertrophy", "conditioning"],
    minMinutes: 35,
    needsLoad: true,
    fatigue: 2,
    dense: false,
    glue: "Keep the conditioning block aerobic and low-impact (bike, rower, ski erg) so it does not eat into recovery from the hypertrophy work.",
  },
  {
    id: "antagonist-supersets",
    name: "Antagonist supersets",
    styles: ["superset"],
    minMinutes: 20,
    needsLoad: false,
    fatigue: 2,
    dense: true,
    glue: "Pair push with pull and upper with lower so every muscle rests while another works, and cover the whole body across the pairs.",
  },
  {
    id: "emom-ladder",
    name: "EMOM ladder",
    styles: ["emom"],
    minMinutes: 15,
    needsLoad: false,
    fatigue: 2,
    dense: true,
    glue: "Rotate 2-4 stations across the windows so the ladder covers a lower-body movement, a push, a pull and core.",
  },
  {
    id: "functional-chipper",
    name: "Functional chipper",
    styles: ["functional", "amrap"],
    minMinutes: 25,
    needsLoad: false,
    fatigue: 3,
    dense: true,
    glue: "The AMRAP closes the session with movements from the functional block at lighter loads, so the whole session builds to one hard effort.",
  },
  {
    id: "full-body-circuit",
    name: "Full-body circuit",
    styles: ["circuit", "functional"],
    minMinutes: 20,
    needsLoad: false,
    fatigue: 2,
    dense: true,
    glue: "Make the two blocks different in character: one slower and strength-biased, one faster and more athletic. Together they cover every major movement pattern.",
  },
  {
    id: "strength-tabata",
    name: "Strength + Tabata finisher",
    styles: ["strength", "tabata"],
    minMinutes: 30,
    needsLoad: true,
    fatigue: 3,
    dense: false,
    glue: "The Tabata is the only finisher, on a bike, rower or with bodyweight, after the strength work.",
  },
  {
    id: "aerobic-accessory",
    name: "Aerobic base + accessories",
    styles: ["cardio", "hypertrophy"],
    minMinutes: 35,
    needsLoad: false,
    fatigue: 2,
    dense: false,
    glue: "Point the accessory work at muscles the cardio does not use — upper body and core after running or cycling.",
  },
  {
    id: "vo2-intervals",
    name: "VO2max intervals",
    styles: ["hiit"],
    minMinutes: 25,
    needsLoad: false,
    fatigue: 3,
    dense: false,
    // HIIT defaults to the anaerobic target; this shape is the aerobic one.
    trains: ["aerobic"],
    glue: "Build the HIIT block as AEROBIC POWER (VO2max Intervals), not anaerobic intervals.",
  },
  {
    id: "easy-aerobic-mobility",
    name: "Easy aerobic + mobility",
    styles: ["cardio", "flexibility"],
    minMinutes: 20,
    needsLoad: false,
    fatigue: 1,
    dense: false,
    glue: "Easy conversational cardio, then a short mobility block for whatever feels tight. Nothing hard.",
  },
];

// Loaded, low-rep, recovery-heavy work that wants a fresh athlete.
const ANCHOR_STYLES = ["strength", "power", "hypertrophy", "superset"];

// Metabolic formats that work as finishers or as the session's whole engine.
const METABOLIC_STYLES = [
  "hiit",
  "tabata",
  "emom",
  "amrap",
  "circuit",
  "conditioning",
];

// Specs that exist in prompts.js with no UI chip to reach them. When the user
// has already picked a spread across both families, this is the pool that
// actually adds something they could not have selected themselves.
const UI_UNREACHABLE_STYLES = ["emom", "amrap", "tabata", "superset"];

// Complement ids are namespaced so a held selection can be distinguished from
// an archetype id when it comes back round on a regenerate.
const COMPLEMENT_PREFIX = "complement:";

// Above this many explicit styles, a selector contributes nothing and drops
// out. A complement rounds out a focused pick ("strength, plus something to
// finish on"); bolted onto a selection that already spans several styles it
// just adds a block to a session that cannot fit the ones it has.
const MAX_COMPLEMENT_BASE = 2;

// Staleness is counted in days and capped, so "never trained" cannot swamp the
// preference bonus — a shape untouched for a fortnight is simply "stale".
const STALENESS_CAP_DAYS = 14;
const DENSE_PREFERENCE_BONUS_DAYS = 3;
const DENSE_PREFERENCE_BONUS_NEED = 0.3;
// Surprise me leans towards the user's goal: up to this many "days" of
// staleness for shapes that train what the goal's weekly targets ask for, and
// half of it off for shapes that train something the goal doesn't target. For
// someone new (no history, so every shape is equally stale) this decides the
// pick; with history, staleness still dominates.
const GOAL_FIT_BONUS_DAYS = 6;

const LAST_ARCHETYPE_KEY = "khronos_last_mixed_archetype";

// Kit that adds no external load. Anything else counts as load.
const NON_LOAD_EQUIPMENT = /^(none|bodyweight|body weight|mat|yoga mat|floor)$/i;

const normaliseStyles = (styles) => {
  const list = Array.isArray(styles) ? styles : styles ? [styles] : [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    if (typeof raw !== "string") continue;
    const value = raw.trim().toLowerCase();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
};

/** True when the equipment list includes anything that adds external load. */
export const hasLoadEquipment = (equipment) => {
  const list = Array.isArray(equipment)
    ? equipment
    : typeof equipment === "string"
      ? equipment.split(",")
      : [];
  return list.some((item) => {
    const value = String(item || "").trim();
    return value && !NON_LOAD_EQUIPMENT.test(value);
  });
};

/** Display name for a shape id, including complements. Null if unknown. */
export const archetypeName = (id) => {
  if (typeof id !== "string") return null;
  if (id.startsWith(COMPLEMENT_PREFIX)) {
    return `plus ${formatLabel(id.slice(COMPLEMENT_PREFIX.length))}`;
  }
  return MIXED_ARCHETYPES.find((a) => a.id === id)?.name || null;
};

/** The weekly-target adaptations a shape trains. */
const trainsOf = (archetype) =>
  archetype.trains ||
  [...new Set(archetype.styles.map((s) => STYLE_ADAPTATION[s]).filter(Boolean))];

/**
 * Days since a style was last trained, per the history summary, or Infinity
 * when it does not appear at all.
 *
 * The match is a substring test against `workoutType` because that field is
 * free text — it may be a stored style key, a joined list, or the plan's own
 * label ("Strength + metcon finisher"). A loose match that occasionally fires
 * on a label is the right trade here: the cost of a false positive is one
 * shape being passed over, not a wrong session.
 */
const daysSinceStyle = (style, history) => {
  let best = Infinity;
  for (const entry of history) {
    const haystack = String(entry?.workoutType || "").toLowerCase();
    if (!haystack.includes(style)) continue;
    const days = Number.isFinite(entry?.daysAgo) ? entry.daysAgo : Infinity;
    if (days < best) best = days;
  }
  return best;
};

/**
 * How stale a shape is: the *most recent* of its component styles, capped. A
 * shape is only as fresh as its freshest-trained part — pairing a style trained
 * yesterday with one untouched for a month should not read as novel.
 */
const stalenessScore = (archetype, history) =>
  Math.min(
    STALENESS_CAP_DAYS,
    archetype.styles.reduce(
      (worst, style) => Math.min(worst, daysSinceStyle(style, history)),
      Infinity,
    ),
  );

/** How well a shape fits the goal's weekly targets, in staleness "days". */
const goalFit = (archetype, targets) => {
  if (!targets) return 0;
  const total = Object.values(targets).reduce((sum, n) => sum + (n || 0), 0);
  if (total === 0) return 0;
  return trainsOf(archetype).reduce((score, tag) => {
    const n = targets[tag] || 0;
    return n > 0
      ? score + (n / total) * GOAL_FIT_BONUS_DAYS
      : score - GOAL_FIT_BONUS_DAYS / 2;
  }, 0);
};

/** Highest-scoring candidate, ties broken randomly. */
const pickBest = (candidates, scoreOf, random) => {
  if (candidates.length === 0) return null;
  let best = -Infinity;
  let tied = [];
  for (const candidate of candidates) {
    const score = scoreOf(candidate);
    if (score > best) {
      best = score;
      tied = [candidate];
    } else if (score === best) {
      tied.push(candidate);
    }
  }
  return tied[Math.floor(random() * tied.length) % tied.length];
};

const complementPoolFor = (selected) => {
  const isAnchor = (s) => ANCHOR_STYLES.includes(s);
  const isMetabolic = (s) => METABOLIC_STYLES.includes(s);

  // All loaded work and no engine — it wants a finisher.
  if (selected.every(isAnchor)) return METABOLIC_STYLES;
  // All engine and no loaded work — it wants an anchor.
  if (selected.every((s) => isMetabolic(s) || s === "cardio"))
    return ANCHOR_STYLES;
  // A spread across both: add something they could not have picked.
  return UI_UNREACHABLE_STYLES;
};

/**
 * Narrow the library to shapes that fit today, relaxing in steps rather than
 * returning nothing: exclusions go first (a repeat beats no session), then the
 * situational limits. Hidden shapes are never brought back — the user said no.
 */
const eligibleArchetypes = ({
  sessionMinutes,
  hasLoad,
  readiness,
  noFaff,
  blocked,
  excluded,
}) => {
  const notBlocked = MIXED_ARCHETYPES.filter((a) => !blocked.has(a.id));
  const fits = (a) =>
    (!Number.isFinite(sessionMinutes) || sessionMinutes >= a.minMinutes) &&
    (hasLoad !== false || !a.needsLoad) &&
    (readiness !== "Low Energy" || a.fatigue < 3) &&
    // Heavy loaded shapes mean barbells and plate changes — the opposite of
    // a no-faff session.
    (!noFaff || !a.needsLoad);
  const fitting = notBlocked.filter(fits);
  const fresh = fitting.filter((a) => !excluded.has(a.id));
  if (fresh.length) return fresh;
  if (fitting.length) return fitting;
  const freshAny = notBlocked.filter((a) => !excluded.has(a.id));
  if (freshAny.length) return freshAny;
  return notBlocked.length ? notBlocked : MIXED_ARCHETYPES;
};

const wantsDense = (preferences) =>
  preferences?.restStyle === "paired" || preferences?.sessionPace === "dense";

const describeArchetype = (archetype, mode, extra = {}) => ({
  id: archetype.id,
  name: archetype.name,
  mode,
  glue: archetype.glue || null,
  reason: extra.reason || null,
});

/**
 * Resolve a style selection that may contain a selector ("mixed" or "next")
 * into concrete styles.
 *
 * Returns `{ styles, archetype }`, where `archetype` is
 * `{ id, name, mode, glue, reason }` when something was chosen and null when
 * nothing was added.
 *
 * Three cases:
 *   - Selector alone          -> a shape's styles.
 *   - Selector + other styles -> those styles plus ONE complement.
 *   - Selector + many styles  -> the selector simply drops out.
 *
 * "next" wins over "mixed" when both are present: asking for what you need is
 * the more specific request.
 */
export const resolveMixedStyle = (selectedStyles, options = {}) => {
  const {
    history = [],
    heldArchetypeId = null,
    excludeArchetypeId = null,
    excludeArchetypeIds = [],
    blockedArchetypeIds = [],
    sessionMinutes,
    hasLoad,
    readiness,
    preferences,
    muscleSets = null,
    noFaff = false,
    // Goal-aware sessions a week per tag (utils/weeklyTargets.js); the
    // general targets when absent.
    targets,
    random = Math.random,
  } = options;

  // An empty selection is a request for *a session*, with no opinion about its
  // shape — which is what Surprise me means. Treating it as such closes the
  // last route to the vague fallback instruction in prompts.js.
  const selected = normaliseStyles(selectedStyles);
  if (selected.length === 0) selected.push(MIXED_STYLE);
  const hasSelector = selected.some((s) => SELECTOR_STYLES.includes(s));
  if (!hasSelector) {
    return { styles: selected, archetype: null };
  }

  const mode = selected.includes(NEXT_STYLE) ? "next" : "surprise";
  const others = selected.filter((s) => !SELECTOR_STYLES.includes(s));
  const safeHistory = Array.isArray(history) ? history : [];
  const balance = summariseWeeklyBalance(safeHistory, targets);
  const excluded = new Set(
    [excludeArchetypeId, ...(excludeArchetypeIds || [])].filter(Boolean),
  );

  // --- Selector alongside explicit styles: add one complement ---
  if (others.length > 0) {
    if (others.length > MAX_COMPLEMENT_BASE) {
      return { styles: others, archetype: null };
    }
    const held =
      typeof heldArchetypeId === "string" &&
      heldArchetypeId.startsWith(COMPLEMENT_PREFIX)
        ? heldArchetypeId.slice(COMPLEMENT_PREFIX.length)
        : null;
    const complementOf = (style) => ({
      id: `${COMPLEMENT_PREFIX}${style}`,
      name: `plus ${formatLabel(style)}`,
      mode,
      glue: `The added ${formatLabel(style)} block complements the chosen styles: it trains movement patterns and muscles the other blocks do not load.`,
      reason:
        mode === "next"
          ? describeNeeds(balance, [STYLE_ADAPTATION[style]].filter(Boolean))
          : null,
    });
    if (held && !others.includes(held)) {
      return { styles: [...others, held], archetype: complementOf(held) };
    }

    const pool = complementPoolFor(others).filter((s) => !others.includes(s));
    // Every preferred candidate already selected — widen before giving up, so
    // "one of everything bar Superset" still gets its complement.
    const widened = pool.length
      ? pool
      : [...ANCHOR_STYLES, ...METABOLIC_STYLES].filter(
          (s) => !others.includes(s),
        );
    const notExcluded = widened.filter(
      (s) => !excluded.has(`${COMPLEMENT_PREFIX}${s}`),
    );
    const candidates = notExcluded.length ? notExcluded : widened;
    const staleness = (style) =>
      Math.min(STALENESS_CAP_DAYS, daysSinceStyle(style, safeHistory));
    const complement = pickBest(
      candidates,
      mode === "next"
        ? (style) =>
            needFor(balance, STYLE_ADAPTATION[style]) * 100 + staleness(style)
        : staleness,
      random,
    );
    // Nothing left to add (the user ticked the lot) — the selector drops out.
    if (!complement) return { styles: others, archetype: null };

    return { styles: [...others, complement], archetype: complementOf(complement) };
  }

  // --- Selector on its own: resolve to a full shape ---
  const heldArchetype = MIXED_ARCHETYPES.find((a) => a.id === heldArchetypeId);
  if (heldArchetype) {
    return {
      styles: [...heldArchetype.styles],
      archetype: describeArchetype(heldArchetype, mode, {
        reason:
          mode === "next"
            ? describeNeeds(balance, trainsOf(heldArchetype))
            : null,
      }),
    };
  }

  const pool = eligibleArchetypes({
    sessionMinutes,
    hasLoad,
    readiness,
    noFaff,
    blocked: new Set(blockedArchetypeIds || []),
    excluded,
  });
  const dense = wantsDense(preferences) || noFaff;

  let chosen;
  if (mode === "next") {
    // Need first; staleness breaks ties so a balanced week still rotates.
    // Low energy also leans toward the easier shapes that remain.
    const scoreOf = (a) =>
      trainsOf(a).reduce((sum, tag) => sum + needFor(balance, tag), 0) +
      (dense && a.dense ? DENSE_PREFERENCE_BONUS_NEED : 0) -
      (readiness === "Low Energy" ? (a.fatigue - 1) * 0.25 : 0) +
      stalenessScore(a, safeHistory) / 100;
    chosen = pickBest(pool, scoreOf, random);
  } else {
    const scoreOf = (a) =>
      stalenessScore(a, safeHistory) +
      (dense && a.dense ? DENSE_PREFERENCE_BONUS_DAYS : 0) +
      goalFit(a, targets);
    chosen = pickBest(pool, scoreOf, random);
  }
  chosen = chosen || MIXED_ARCHETYPES[0];

  // For a lifting shape, name the muscles that are behind this week too.
  const muscleNote =
    mode === "next" && trainsOf(chosen).includes("resistance")
      ? describeUndertrained(muscleSets)
      : null;
  const reason =
    mode === "next"
      ? [
          describeNeeds(balance, trainsOf(chosen)) ||
            "Your week is on track, so this rotates in something you haven't done lately.",
          muscleNote,
        ]
          .filter(Boolean)
          .join(" ")
      : null;

  return {
    styles: [...chosen.styles],
    archetype: describeArchetype(chosen, mode, { reason }),
  };
};

/**
 * The shape used by the previous generation, so the next rotation can avoid
 * it. Device-local and deliberately not a workout *setting*: it is rotation
 * bookkeeping, not a preference anyone would want synced to their account.
 */
export const readLastArchetypeId = () => {
  try {
    return localStorage.getItem(LAST_ARCHETYPE_KEY) || null;
  } catch {
    return null;
  }
};

export const rememberArchetypeId = (id) => {
  if (!id) return;
  try {
    localStorage.setItem(LAST_ARCHETYPE_KEY, id);
  } catch {
    // localStorage full or unavailable — rotation falls back to history alone.
  }
};
